type RoleLike = {
  role?: unknown;
};

type AdminAccessState = {
  hasAdminAccess?: boolean;
  role?: unknown;
  sessionRoles?: unknown;
  forms?: unknown;
};

type AdminPreviewState = AdminAccessState & {
  search?: string;
  selectedFormId?: string;
  allowPendingAccessCheck?: boolean;
};

const trueLikeValues = new Set(['1', 'true', 'yes', 'y']);
const falseLikeValues = new Set(['0', 'false', 'no', 'n']);

const normalizeRole = (value: unknown): string => String(value || '').trim().toLowerCase();

export const parseOptionalBoolValue = (value: string | null): boolean | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  if (trueLikeValues.has(normalized)) return true;
  if (falseLikeValues.has(normalized)) return false;
  return undefined;
};

export const hasAdminSessionAccess = ({
  hasAdminAccess,
  role,
  sessionRoles,
  forms,
}: AdminAccessState): boolean => {
  if (hasAdminAccess === true) return true;

  const roleSet = new Set<string>();
  const currentRole = normalizeRole(role);
  if (currentRole) roleSet.add(currentRole);

  if (Array.isArray(sessionRoles)) {
    sessionRoles.forEach((sessionRole) => {
      const normalized = normalizeRole(sessionRole);
      if (normalized) roleSet.add(normalized);
    });
  }

  if (Array.isArray(forms)) {
    forms.forEach((form) => {
      const normalized = normalizeRole((form as RoleLike | null | undefined)?.role);
      if (normalized) roleSet.add(normalized);
    });
  }

  return roleSet.has('admin');
};

export const isAdminPreviewRequest = ({
  search,
  selectedFormId,
}: Pick<AdminPreviewState, 'search' | 'selectedFormId'>): boolean =>
  parseOptionalBoolValue(new URLSearchParams(search || '').get('admin')) === true &&
  Boolean(selectedFormId);

export const isAdminPreviewMode = ({
  search,
  selectedFormId,
  allowPendingAccessCheck = false,
  ...accessState
}: AdminPreviewState): boolean => {
  if (!isAdminPreviewRequest({ search, selectedFormId })) return false;

  const hasResolvedAccessState =
    accessState.hasAdminAccess === true ||
    normalizeRole(accessState.role).length > 0 ||
    Array.isArray(accessState.sessionRoles) ||
    Array.isArray(accessState.forms);

  return (
    hasAdminSessionAccess(accessState) ||
    (allowPendingAccessCheck && !hasResolvedAccessState)
  );
};

export const withAdminPreviewSearch = (path: string, enabled: boolean): string => {
  if (!enabled) return path;

  const [basePath, hash = ''] = String(path || '').split('#', 2);
  const separator = basePath.includes('?') ? '&' : '?';
  const nextHash = hash ? `#${hash}` : '';
  return `${basePath}${separator}admin=1${nextHash}`;
};
